'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

export interface WizardStep {
  key: string;
  label: string;
  icon?: React.ReactNode;
}

interface VaccineStepperProps {
  steps: WizardStep[];
  currentIndex: number;
  onStepClick?: (index: number) => void;
}

// Generic, theme-aware progress stepper for the booking wizard. Uses the active
// pharmacy's secondary color via the `hsecondary` dynamic tokens.
export function VaccineStepper({ steps, currentIndex, onStepClick }: VaccineStepperProps) {
  return (
    <div className="w-full">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isActive = index === currentIndex;
          const isCompleted = index < currentIndex;
          const isAccessible = index <= currentIndex;

          return (
            <React.Fragment key={step.key}>
              <div className="flex shrink-0 flex-col items-center">
                <motion.button
                  type="button"
                  onClick={() => isAccessible && onStepClick?.(index)}
                  disabled={!isAccessible}
                  className={`relative flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all duration-300 sm:h-11 sm:w-11
                    ${isActive
                      ? 'border-hsecondary-500 bg-hsecondary-500 text-white shadow-lg'
                      : isCompleted
                        ? 'border-hsecondary-500 bg-hsecondary-500 text-white'
                        : 'border-gray-200 bg-white text-gray-400'}
                    ${isAccessible && !isActive ? 'hover:border-hsecondary-500/50 cursor-pointer' : ''}
                    ${!isAccessible ? 'cursor-not-allowed' : ''}`}
                  whileHover={isAccessible ? { scale: 1.05 } : {}}
                  whileTap={isAccessible ? { scale: 0.95 } : {}}
                  animate={{ scale: isActive ? 1.08 : 1 }}
                >
                  {isCompleted ? (
                    <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 500, damping: 30 }}>
                      <Check className="h-5 w-5" />
                    </motion.span>
                  ) : (
                    step.icon ?? <span className="text-sm font-semibold">{index + 1}</span>
                  )}
                </motion.button>
                <span className={`mt-2 hidden text-xs font-medium transition-colors sm:block ${isActive || isCompleted ? 'text-hsecondary-600' : 'text-gray-400'}`}>
                  {step.label}
                </span>
              </div>

              {index < steps.length - 1 && (
                <div className="mx-1 min-w-0 flex-1 sm:mx-3">
                  <div className="h-0.5 w-full rounded-full bg-gray-100">
                    <motion.div
                      className="h-full rounded-full bg-hsecondary-500"
                      initial={{ width: '0%' }}
                      animate={{ width: index < currentIndex ? '100%' : '0%' }}
                      transition={{ duration: 0.4, ease: 'easeInOut' }}
                    />
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      {/* Mobile: show only the current step label */}
      <p className="mt-3 text-center text-sm font-medium text-gray-700 sm:hidden">{steps[currentIndex]?.label}</p>
    </div>
  );
}
